import { useEffect, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import type { CatalogChanged, CatalogScopeType } from '@localagent/sdk';
import { claimsAtom } from '../stores/auth/atoms';
import { bridgeEventStreamAtom, daemonPushFrameStreamAtom } from '../stores/sdk/atoms';
import { INVALIDATION_KEYS_BY_RESOURCE } from '../shogun/platformCallExecutor';
import { CONTEXT_SOURCES_KEY, HEALTH_KEY } from './useContextSources';

interface CatalogStream {
  registerCatalog(
    scopeType: string,
    scopeId: string,
    handler: (change: CatalogChanged) => void
  ): { registered: Promise<void>; unsubscribe: () => void };
}

type QueryKeyList = ReadonlyArray<readonly string[]>;

function resourceKeys(resource: string): QueryKeyList {
  const keys = INVALIDATION_KEYS_BY_RESOURCE[resource];
  if (!keys) throw new Error(`catalog scope maps to unknown invalidation resource '${resource}'`);
  return keys;
}

const SCOPE_KEYS: Record<CatalogScopeType, () => QueryKeyList> = {
  persona: () => resourceKeys('persona'),
  skills: () => resourceKeys('skills'),
  secrets: () => resourceKeys('secrets'),
  mcp: () => resourceKeys('mcp'),
  agentProcesses: () => resourceKeys('agentProcesses'),
  workspaces: () => resourceKeys('workspaces'),
  sessions: () => [['session-list-pages'], ['session-search-pages']],
  integrations: () => resourceKeys('integrations'),
  orgRoster: () => [['org-roster'], ['account-roles']],
  context: () => [CONTEXT_SOURCES_KEY, HEALTH_KEY],
  billing: () => resourceKeys('billing'),
  work: () => resourceKeys('workManagement'),
  orgchart: () => resourceKeys('orgChart'),
};

function invalidateScope(queryClient: QueryClient, scopeType: CatalogScopeType): void {
  if (scopeType === 'sessions') {
    void queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === 'sessions' && typeof query.queryKey[2] !== 'string',
    });
    // Call-through (sdk ADR-377c6): the fanout marks the SDK's list page
    // windows stale (sdk ADR-b2a2e) — they stay paintable and the read path's
    // unconditional revalidate heals them. Evicting here blanks the return
    // paint (the browse↔search toggle flashes a spinner).
  }
  for (const queryKey of SCOPE_KEYS[scopeType]()) {
    void queryClient.invalidateQueries({ queryKey: [...queryKey] });
  }
}

export function useCatalogSubscription(scopeType: CatalogScopeType, scopeId?: string): void {
  const claims = useAtomValue(claimsAtom);
  const targetId = scopeId ?? claims?.accountId;
  const cloudStream = useAtomValue(bridgeEventStreamAtom);
  const daemonStream = useAtomValue(daemonPushFrameStreamAtom);
  const stream: CatalogStream | null = cloudStream ?? daemonStream;
  const queryClient = useQueryClient();
  const [subscribeError, setSubscribeError] = useState<unknown>(null);

  // A failed subscribe renders the route error boundary, never a silently stale view (app-shell ADR-007).
  if (subscribeError) throw subscribeError;

  useEffect(() => {
    if (!stream || !targetId) return;
    let active = true;
    const { registered, unsubscribe } = stream.registerCatalog(scopeType, targetId, () => {
      invalidateScope(queryClient, scopeType);
    });
    registered.then(
      () => {
        if (active) invalidateScope(queryClient, scopeType);
      },
      (err: unknown) => {
        if (active) setSubscribeError(err);
      },
    );
    return () => {
      active = false;
      unsubscribe();
    };
  }, [stream, targetId, scopeType, queryClient]);
}
